import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ChevronLeftIcon, PlusIcon } from 'lucide-react';
import ScheduleCalendar from '../../components/schedule/ScheduleCalendar';
import ShiftDetailModal from '../../components/schedule/ShiftDetailModal';
import ScheduleStatusBadge from '../../components/schedule/ScheduleStatusBadge';
import ScheduleActions from '../../components/schedule/ScheduleActions';
import CreateShiftModal from '../../components/schedule/CreateShiftModal';
import BulkShiftCreationModal from '../../components/schedule/BulkShiftCreationModal';
import VacationDayInput from '../../components/schedule/VacationDayInput';
import { Card, Button } from '../../components/common';
import Spinner from '../../components/common/Spinner';
import { Shift, ScheduleStatus } from '../../types/schedule';
import { toast } from 'react-hot-toast';
import { useSchedule } from '../../hooks/useSchedule';
import { getTeamMembersWithUserInfo } from '../../lib/api/teamService';
import { useScheduleStore } from '../../lib/store/scheduleStore';

const ScheduleDetailPage: React.FC = () => {
  const { scheduleId, teamId: urlTeamId } = useParams<{ scheduleId: string; teamId: string }>();
  const { schedule, shifts, vacationDays, isLoading, error: scheduleError } = useSchedule(scheduleId);
  const [teamMembers, setTeamMembers] = useState<Array<{ user_id: string; full_name?: string; shift_type: string }>>([]);
  
  const [selectedShift, setSelectedShift] = useState<(Shift & { provider_name?: string }) | null>(null);
  const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
  const [isCreateShiftModalOpen, setIsCreateShiftModalOpen] = useState(false);
  const [isBulkCreateModalOpen, setIsBulkCreateModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  
  // Use the team ID from the schedule if available, otherwise fall back to the URL param
  const teamId = schedule?.team_id || urlTeamId || '';
  
  // Get the store actions directly
  const { 
    fetchSchedule, 
    fetchShifts, 
    fetchVacationDays 
  } = useScheduleStore();
  
  React.useEffect(() => {
    if (teamId) {
      fetchTeamMembers(teamId);
    }
  }, [teamId]);
  
  const fetchTeamMembers = async (id: string) => {
    try {
      const members = await getTeamMembersWithUserInfo(id);
      if (members) {
        // Transform to expected format
        const formattedMembers = members.map(member => ({
          user_id: member.user_id,
          full_name: member.user?.full_name || 'Unknown User',
          shift_type: member.shift_type
        }));

        // Log the team members to debug
        console.log('Team members before setting state:', formattedMembers);
        
        setTeamMembers(formattedMembers);
      }
    } catch (err) {
      console.error('Error fetching team members:', err);
      toast.error('Failed to load team members. Please try again.');
    }
  };
  
  const handleSelectShift = (shift: Shift & { provider_name?: string }) => {
    setSelectedShift(shift);
    setIsShiftModalOpen(true);
  };
  
  const handleSelectSlot = (slotInfo: { start: Date; end: Date; action: string }) => {
    setSelectedDate(slotInfo.start);
    setIsCreateShiftModalOpen(true);
  };
  
  const handleShiftModalClose = () => {
    setIsShiftModalOpen(false);
    setSelectedShift(null);
  };
  
  const handleShiftUpdated = () => {
    // Instead of reloading the entire page, just refresh the data we need
    if (scheduleId) {
      fetchSchedule(scheduleId);
      fetchShifts(scheduleId);
      fetchVacationDays(scheduleId);
      
      // Also refresh team members if needed
      if (teamId) {
        fetchTeamMembers(teamId);
      }
    }
  };
  
  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMMM d, yyyy');
    } catch (formatError) {
      console.error('Error formatting date:', formatError);
      return dateString;
    }
  };
  
  if (isLoading) {
    return <Spinner />;
  }
  
  if (scheduleError || !schedule) {
    return (
      <div className="text-center py-8 text-red-500">
        Error loading schedule. Please try again.
      </div>
    );
  }
  
  const scheduleTitle = `${formatDate(schedule.start_date)} - ${formatDate(schedule.end_date)}`;
  const isEditable = (schedule.status as ScheduleStatus) === 'draft' || (schedule.status as ScheduleStatus) === 'preferences_closed';
  
  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <Link
          to={`/teams/${schedule.team_id}/schedules`}
          className="inline-flex items-center text-primary hover:text-primary-dark"
        >
          <ChevronLeftIcon size={16} className="mr-1" />
          Back to Schedules
        </Link>
      </div>
      
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{scheduleTitle}</h1>
          <div className="flex items-center mt-2">
            <ScheduleStatusBadge status={schedule.status as ScheduleStatus} />
          </div>
        </div>
        
        <ScheduleActions
          schedule={schedule}
          onSuccess={handleShiftUpdated}
        />
      </div>
      
      <div className="py-6">
        <Card>
          {isEditable && (
            <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Schedule Calendar</h3>
              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  leftIcon={<PlusIcon size={16} />}
                  onClick={() => setIsCreateShiftModalOpen(true)}
                >
                  Add Shift
                </Button>
                <Button
                  variant="primary"
                  leftIcon={<PlusIcon size={16} />}
                  onClick={() => setIsBulkCreateModalOpen(true)}
                >
                  Bulk Create Shifts
                </Button>
              </div>
            </div>
          )}
          
          <div className="p-4">
            <ScheduleCalendar
              shifts={shifts}
              onSelectShift={handleSelectShift}
              onSelectSlot={handleSelectSlot}
              isEditable={isEditable}
              onCreateShifts={() => setIsBulkCreateModalOpen(true)}
              vacationDays={vacationDays}
              teamMembers={teamMembers}
            />
          </div>
        </Card>
      </div>
      
      {isEditable && scheduleId && teamId && (
        <div className="mt-6">
          <VacationDayInput
            scheduleId={scheduleId}
            vacationDays={vacationDays}
            teamMembers={teamMembers}
            onVacationAdded={handleShiftUpdated}
            onVacationRemoved={handleShiftUpdated}
          />
        </div>
      )}
      
      <ShiftDetailModal
        shift={selectedShift}
        isOpen={isShiftModalOpen}
        onClose={handleShiftModalClose}
        teamId={teamId}
        onShiftUpdated={handleShiftUpdated}
        readOnly={!isEditable}
      />
      
      {isEditable && scheduleId && (
        <>
          <CreateShiftModal
            isOpen={isCreateShiftModalOpen}
            onClose={() => {
              setIsCreateShiftModalOpen(false);
              setSelectedDate(undefined);
            }}
            scheduleId={scheduleId}
            initialDate={selectedDate}
            providers={teamMembers}
            onShiftCreated={handleShiftUpdated}
          />
          
          <BulkShiftCreationModal
            isOpen={isBulkCreateModalOpen}
            onClose={() => setIsBulkCreateModalOpen(false)}
            scheduleId={scheduleId}
            onShiftsCreated={handleShiftUpdated}
          />
        </>
      )}
    </div>
  );
};

export default ScheduleDetailPage; 